import { createAsyncThunk } from '@reduxjs/toolkit';
import { linkPhotoCommit, linkPhotoFailed, updatePhotoStatus } from '../slices/photosSlice';
import { FileCache } from '@/lib/cache-storage';
import { apiClient } from '@/lib/api-client';
import type { RootState } from '@/store';

// Direct photo upload function (bypasses Redux Offline serialization)
export const directPhotoUpload = async (data: {
  temp_id: string;
  local: string;
  tag: string;
  task_id: string;
  job_id: string;
  asset_id: string;
  fileType?: string;
  storedAt?: string;
}): Promise<any> => {
  console.log('🚀 Direct photo upload starting for:', data.temp_id);

  try {
    // Retrieve the cached file
    const cachedBlob = await FileCache.retrieveFileBlob(data.temp_id);
    if (!cachedBlob) {
      throw new Error(`Photo with ID ${data.temp_id} not found in cache`);
    }

    // Create File object from cached blob
    const file = new File([cachedBlob], data.local, {
      type: data.fileType || 'image/jpeg',
      lastModified: data.storedAt ? new Date(data.storedAt).getTime() : Date.now()
    });

    console.log('✅ File retrieved from cache:', file.name, 'Size:', file.size);

    // Create FormData for upload
    const formData = new FormData();
    formData.append('local', data.local);
    formData.append('task_id', String(data.task_id));
    formData.append('job_id', String(data.job_id));
    formData.append('tag', data.tag);
    formData.append('asset_id', data.asset_id);
    formData.append('temp_id', data.temp_id);
    formData.append('file', file, data.local);

    // Upload directly using apiClient
    const response = await apiClient.post('/upload-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('✅ Photo uploaded successfully:', response.data);

    // Clean up cached file after successful upload
    await FileCache.removeFile(data.temp_id);
    console.log('🧹 Cleaned up cached file:', data.temp_id);

    return response.data;

  } catch (error) {
    console.error('❌ Photo upload failed:', error);
    throw error;
  }
};

// Queue photo upload for when online (uses Redux Offline to trigger function)
export const queuePhotoUpload = (data: {
  temp_id: string;
  local: string;
  tag: string;
  task_id: string;
  job_id: string;
  asset_id: string;
  fileType?: string;
  storedAt?: string;
}) => ({
  type: 'photos/queuePhotoUpload',
  payload: data,
  meta: {
    offline: {
      effect: {
        url: '/trigger-photo-upload', // Special URL to trigger our upload function
        method: 'POST',
        body: data, // Just metadata
      },
      commit: { type: 'photos/queuePhotoUploadCommit', meta: { temp_id: data.temp_id } },
      rollback: { type: 'photos/queuePhotoUploadRollback', meta: { temp_id: data.temp_id } },
    },
  },
});

// Direct upload function (for immediate uploads when online)
export const uploadPhoto = createAsyncThunk<
  void,
  {
    temp_id: string;
    local: string;
    tag: string;
    task_id: string;
    job_id: string;
    asset_id: string;
    fileType?: string;
    storedAt?: string;
  },
  { state: RootState }
>(
  'photos/uploadPhoto',
  async (photoData, { dispatch, getState }) => {
    const { temp_id, local, tag, task_id, job_id, asset_id, fileType, storedAt } = photoData;

    try {
      console.log('🚀 Starting photo upload for:', temp_id);

      // Update status to uploading
      dispatch(updatePhotoStatus({
        temp_id,
        status: 'uploading',
        progress: 0
      }));

      // Retrieve the cached file
      const cachedBlob = await FileCache.retrieveFileBlob(temp_id);
      if (!cachedBlob) {
        throw new Error(`Photo with ID ${temp_id} not found in cache`);
      }

      // Create File object from cached blob
      const file = new File([cachedBlob], local, {
        type: fileType || 'image/jpeg',
        lastModified: storedAt ? new Date(storedAt).getTime() : Date.now()
      });

      console.log('✅ Retrieved cached file for upload:', temp_id, 'Size:', file.size);

      // Create FormData for upload
      const formData = new FormData();
      formData.append('local', local);
      formData.append('task_id', task_id);
      formData.append('job_id', job_id);
      formData.append('tag', tag);
      formData.append('asset_id', asset_id);
      formData.append('temp_id', temp_id);
      formData.append('file', file, local);

      // Update progress
      dispatch(updatePhotoStatus({
        temp_id,
        status: 'uploading',
        progress: 50
      }));

      // Upload directly using apiClient
      const response = await apiClient.post('/upload-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('✅ Photo uploaded successfully:', response.data);

      // Update status to completed
      dispatch(updatePhotoStatus({
        temp_id,
        status: 'completed',
        progress: 100
      }));

      // Cleanup cached file
      await FileCache.removeFile(temp_id);
      console.log('🧹 Cleaned up cached file:', temp_id);

    } catch (error) {
      console.error('❌ Photo upload failed:', error);

      // Update status to error
      dispatch(updatePhotoStatus({
        temp_id,
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed'
      }));

      throw error;
    }
  }
);

// Synchronous action to add photo metadata to Redux (no File object)
export const linkPhoto = (data: any) => ({
  type: 'photos/linkPhoto',
  payload: {
    // Only serializable data - no File objects
    local: data.local,
    asset_id: data.asset_id,
    uri: data.uri,
    originalLocal: data.originalLocal,
    original_asset_id: data.original_asset_id,
    originalUri: data.originalUri,
    tag: data.tag,
    task_id: data.task_id,
    job_id: data.job_id,
    temp_id: data.temp_id,
    // Cache metadata
    cached: data.cached,
    fileSize: data.fileSize,
    fileType: data.fileType,
    storedAt: data.storedAt,
  },
});

export const unlinkPhoto = (data: any) => ({
  type: 'photos/unlinkPhoto',
  payload: data,
});